import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import SydSelect from '../../../commonDesign/SydSelect'
import SydButton from '../../../commonDesign/Button'
import { CALC_SOURCES, COLUMN_CATALOG, COLUMN_CATALOG_MAP, createColumnInstance } from '../lib/constants'

// Catalog-driven measure picker used by chart tools when the user
// wants to escape the simple axis / measure dropdown and pull in
// something that's neither in their table nor in the chart's
// preset list. Self-contained:
//
//   - `isEligible(catalogEntry)` filters which measure TYPES show up
//     (e.g. scatter restricts to material catalog entries with a
//     numeric measureKind; allocation restricts to currency, etc.)
//   - Categories down the left, measures in the chosen category on
//     the right. For configurable measures (groupStatistic,
//     performance, activity, etc.) a small options form appears
//     below the measure list and pre-fills with the calcSource's
//     `defaultOptions`.
//   - On confirm, builds a fresh column instance via
//     `createColumnInstance` and hands it to `onPick`. Caller is
//     responsible for adding the instance to its own state so the
//     measure stays available in the picker dropdown for swapping.
//
// Reusable across Scatter / Allocation / Trend -- only the
// eligibility predicate differs.

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minHeight: 500,
    maxHeight: '85vh',
    minWidth: 720,
    maxWidth: 880
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--dim-2, 8px)',
    padding: 'var(--dim-3, 12px) var(--dim-4, 16px)',
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  titleText: {
    flex: 1,
    fontSize: 15,
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-neutral-900, #111827)'
  },
  content: {
    padding: 0,
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden'
  },
  leftRail: {
    width: 200,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflow: 'auto',
    background: 'var(--theme-color-surface-d1, #fafafa)'
  },
  categoryItem: {
    display: 'block',
    width: '100%',
    padding: 'var(--dim-2, 8px) 14px',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    borderLeft: '3px solid transparent',
    fontFamily: 'inherit',
    fontSize: 13,
    color: 'var(--color-neutral-700, #555)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--color-primary-50, #f0f7ff)'
    }
  },
  categoryItemActive: {
    background: 'var(--color-primary-50, #f0f7ff)',
    borderLeftColor: 'var(--color-primary-500, #1a73e8)',
    color: 'var(--color-primary-700, #1557b0)',
    fontWeight: 'var(--font-weight-semibold, 600)'
  },
  rightPane: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  measureList: {
    flex: 1,
    overflow: 'auto',
    padding: 'var(--dim-1, 4px) 0'
  },
  measureItem: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '10px var(--dim-4, 16px)',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    borderTop: '1px solid transparent',
    borderBottom: '1px solid transparent',
    fontFamily: 'inherit',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--color-primary-50, #f0f7ff)'
    }
  },
  measureItemActive: {
    background: 'var(--color-primary-50, #f0f7ff)',
    borderTop: '1px solid var(--color-primary-200, #b8d1fa)',
    borderBottom: '1px solid var(--color-primary-200, #b8d1fa)'
  },
  measureLabel: {
    fontSize: 13,
    fontWeight: 'var(--font-weight-medium, 500)',
    color: 'var(--color-neutral-900, #111827)'
  },
  measureSub: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #6b7280)',
    marginTop: 2
  },
  emptyMessage: {
    padding: 'var(--dim-lg, 24px)',
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13,
    textAlign: 'center'
  },
  optionsForm: {
    flexShrink: 0,
    padding: 14,
    borderTop: `1px solid ${theme.palette.divider}`,
    background: 'var(--theme-color-surface-d1, #fafafa)',
    maxHeight: 220,
    overflow: 'auto'
  },
  optionsFormTitle: {
    fontSize: 11,
    fontWeight: 'var(--font-weight-bold, 700)',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: 'var(--color-neutral-500, #6b7280)',
    marginBottom: 'var(--dim-2, 8px)'
  },
  field: {
    marginBottom: 10
  },
  fieldLabel: {
    fontSize: 11,
    color: 'var(--color-neutral-600, #4b5563)',
    marginBottom: 'var(--dim-1, 4px)'
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: 'var(--color-neutral-800, #222)',
    cursor: 'pointer'
  },
  multiselect: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 'var(--dim-1, 4px) var(--dim-3, 12px)'
  }
}))

function getSettingsSchema (catalogEntry) {
  if (!catalogEntry) return []
  // Material columns get their settings from the CALC_SOURCES table.
  const source = catalogEntry.calcSource ? CALC_SOURCES[catalogEntry.calcSource] : null
  return source?.settingsSchema || catalogEntry.settingsSchema || []
}

function getDefaultOptions (catalogEntry) {
  if (!catalogEntry) return {}
  const source = catalogEntry.calcSource ? CALC_SOURCES[catalogEntry.calcSource] : null
  return { ...(source?.defaultOptions || {}), ...(catalogEntry.defaultOptions || {}) }
}

function MeasureField ({ setting, value, onChange, classes }) {
  const effective = value ?? setting.defaultValue
  if (setting.control === 'select') {
    return (
      <div className={classes.field}>
        <div className={classes.fieldLabel}>{setting.label}</div>
        <SydSelect
          options={setting.options}
          value={effective}
          onChange={(e) => onChange(e.target.value)}
          size='sm'
          minWidth='220px'
        />
      </div>
    )
  }
  if (setting.control === 'checkbox') {
    return (
      <label className={classes.checkboxRow}>
        <input
          type='checkbox'
          checked={!!effective}
          onChange={(e) => onChange(e.target.checked)}
        />
        {setting.label}
      </label>
    )
  }
  if (setting.control === 'multiselect') {
    const selected = Array.isArray(effective) ? effective : []
    const toggle = (optValue) => {
      const next = selected.includes(optValue)
        ? selected.filter(v => v !== optValue)
        : [...selected, optValue]
      onChange(next)
    }
    return (
      <div className={classes.field}>
        <div className={classes.fieldLabel}>{setting.label}</div>
        <div className={classes.multiselect}>
          {setting.options.map(opt => (
            <label key={opt.value} className={classes.checkboxRow}>
              <input
                type='checkbox'
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>
    )
  }
  return null
}

MeasureField.propTypes = {
  setting: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    control: PropTypes.oneOf(['select', 'checkbox', 'multiselect']).isRequired,
    options: PropTypes.array,
    defaultValue: PropTypes.any
  }).isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}

export default function MeasurePickerDialog ({
  open,
  onClose,
  onPick,
  title = 'Choose a measure',
  isEligible
}) {
  const classes = useStyles()

  // Catalog entries we'll show, grouped by category. Recomputed only
  // when isEligible changes (cheap, runs O(n) over ~80 entries).
  const { categories, byCategory } = useMemo(() => {
    const eligible = COLUMN_CATALOG.filter(entry => {
      if (entry.kind && entry.kind !== 'material') return false
      return isEligible ? isEligible(entry) : true
    })
    const grouped = new Map()
    for (const entry of eligible) {
      const cat = entry.category || 'Other'
      if (!grouped.has(cat)) grouped.set(cat, [])
      grouped.get(cat).push(entry)
    }
    return { categories: Array.from(grouped.keys()).sort(), byCategory: grouped }
  }, [isEligible])

  const [activeCategory, setActiveCategory] = useState(null)
  const [activeColumnType, setActiveColumnType] = useState(null)
  const [optionsDraft, setOptionsDraft] = useState({})

  // Reset selection whenever the dialog opens. Snapping the active
  // category to the first available one (rather than null) saves the
  // user a click in the common case.
  useEffect(() => {
    if (!open) return
    const firstCategory = categories[0] || null
    setActiveCategory(firstCategory)
    setActiveColumnType(null)
    setOptionsDraft({})
  }, [open, categories])

  const activeEntry = activeColumnType ? COLUMN_CATALOG_MAP[activeColumnType] : null
  const activeSchema = useMemo(() => getSettingsSchema(activeEntry), [activeEntry])

  const handleSelectMeasure = useCallback((columnType) => {
    const entry = COLUMN_CATALOG_MAP[columnType]
    setActiveColumnType(columnType)
    setOptionsDraft(getDefaultOptions(entry))
  }, [])

  const handleSettingChange = useCallback((key, next) => {
    setOptionsDraft(prev => ({ ...prev, [key]: next }))
  }, [])

  const handleConfirm = useCallback(() => {
    if (!activeColumnType) return
    const instance = createColumnInstance(activeColumnType, optionsDraft)
    if (!instance) return
    onPick(instance)
    onClose()
  }, [activeColumnType, optionsDraft, onPick, onClose])

  const measuresInCategory = activeCategory ? (byCategory.get(activeCategory) || []) : []

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth={false}
    >
      <DialogTitle disableTypography className={classes.title}>
        <span className={classes.titleText}>{title}</span>
        <IconButton size='small' onClick={onClose} aria-label='Close'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.leftRail}>
          {categories.length === 0 && (
            <div className={classes.emptyMessage}>No measures available.</div>
          )}
          {categories.map(cat => (
            <button
              key={cat}
              type='button'
              className={`${classes.categoryItem} ${cat === activeCategory ? classes.categoryItemActive : ''}`}
              onClick={() => { setActiveCategory(cat); setActiveColumnType(null); setOptionsDraft({}) }}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className={classes.rightPane}>
          <div className={classes.measureList}>
            {measuresInCategory.length === 0 && (
              <div className={classes.emptyMessage}>Pick a category to see measures.</div>
            )}
            {measuresInCategory.map(entry => {
              const isActive = entry.columnType === activeColumnType
              return (
                <button
                  key={entry.columnType}
                  type='button'
                  className={`${classes.measureItem} ${isActive ? classes.measureItemActive : ''}`}
                  onClick={() => handleSelectMeasure(entry.columnType)}
                >
                  <span className={classes.measureLabel}>{entry.label}</span>
                  <span className={classes.measureSub}>{entry.columnType}</span>
                </button>
              )
            })}
          </div>
          {activeEntry && activeSchema.length > 0 && (
            <div className={classes.optionsForm}>
              <div className={classes.optionsFormTitle}>Options</div>
              {activeSchema.map(setting => (
                <MeasureField
                  key={setting.key}
                  setting={setting}
                  value={optionsDraft[setting.key]}
                  onChange={(next) => handleSettingChange(setting.key, next)}
                  classes={classes}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <SydButton variant='outline' size='sm' onClick={onClose}>Cancel</SydButton>
        <SydButton
          variant='primary'
          size='sm'
          disabled={!activeColumnType}
          onClick={handleConfirm}
        >
          Use measure
        </SydButton>
      </DialogActions>
    </Dialog>
  )
}

MeasurePickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  title: PropTypes.string,
  // (catalogEntry) => boolean. Receives the COLUMN_CATALOG entry; the
  // dialog already filters out non-material kinds. Return true to
  // include the type in the picker. Omit to allow everything material.
  isEligible: PropTypes.func
}
